import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Box,
  Spinner,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useUkraineRegions } from '../hooks/useAlerts';

interface RegionPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (region: { regionId: string; regionName: string }) => void;
}

export function RegionPickerModal({ isOpen, onClose, onSelect }: RegionPickerModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: regions, isLoading } = useUkraineRegions();

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const filtered = useMemo(() => {
    const list = regions ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list.slice(0, 100);
    return list
      .filter(
        (r) =>
          r.regionName.toLowerCase().includes(q) ||
          (r.regionEngName ? r.regionEngName.toLowerCase().includes(q) : false),
      )
      .slice(0, 200);
  }, [regions, search]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{t('alerts.selectRegion')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('alerts.searchRegion')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </InputGroup>

          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          ) : filtered.length === 0 ? (
            <Text color="gray.400" py={8} textAlign="center">
              {t('alerts.noRegions')}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {filtered.map((r) => (
                <HStack
                  key={r.regionId}
                  py={2.5}
                  px={2}
                  spacing={3}
                  _hover={{ bg: 'gray.50' }}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => {
                    onSelect({ regionId: r.regionId, regionName: r.regionName });
                    onClose();
                  }}
                >
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" color="gray.800" noOfLines={1}>
                      {r.regionName}
                    </Text>
                  </Box>
                  <Badge colorScheme="gray" fontSize="0.6rem" textTransform="capitalize">
                    {r.regionType}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default RegionPickerModal;
